/**
 * Pure parser for the Bulk Add Transactions flow. Splits free-form
 * pasted text into one ParsedDraft per line, extracting an amount,
 * description, and an optional category. Never throws — invalid lines
 * come back with `valid: false` and a `warning` so the UI can surface
 * them and let the user fix or skip.
 */

import type { Category, TransactionType } from '../data/types';
import { suggestCategoryFromMerchant } from './merchantCategories';

export interface ParsedDraft {
  rawLine: string;
  description: string;
  amount: number | null;
  /** Direct match against a user category name on this line. */
  categoryId: string | null;
  /** Keyword categorizer guess from the description. Display-only. */
  suggestedCategoryId: string | null;
  type: TransactionType;
  valid: boolean;
  warning?: string;
}

const AMOUNT_RE = /\$?(\d{1,6}(?:\.\d{1,2})?)/;

/**
 * Parse one line into a draft. Whitespace-trimmed.
 *   - First number-with-optional-decimal-and-$ becomes the amount.
 *   - Leading "+" or a "-" directly preceding the amount flips type.
 *   - Description is the text before the amount (or after if before is empty).
 *   - Category is the first remaining token that case-insensitively
 *     matches a user category name.
 */
function parseLine(rawLine: string, userCategories: Category[]): ParsedDraft {
  const trimmed = rawLine.trim();
  if (!trimmed) {
    return {
      rawLine,
      description: '',
      amount: null,
      categoryId: null,
      suggestedCategoryId: null,
      type: 'expense',
      valid: false,
      warning: 'Empty line',
    };
  }

  // Detect explicit type marker — "+" prefix means income; a "-"
  // preceding the amount also means income (a paycheck deposit). The
  // default is expense.
  let type: TransactionType = 'expense';
  if (trimmed.startsWith('+')) type = 'income';

  const match = trimmed.match(AMOUNT_RE);
  if (!match) {
    return {
      rawLine,
      description: trimmed,
      amount: null,
      categoryId: null,
      suggestedCategoryId: null,
      type,
      valid: false,
      warning: 'No number found',
    };
  }

  const matchIndex = match.index ?? 0;
  const matchEnd = matchIndex + match[0].length;
  const amount = Number(match[1]);

  // Check the character immediately before the matched amount for a
  // sign indicator. "-100 Paycheck" → income.
  if (matchIndex > 0 && trimmed[matchIndex - 1] === '-') {
    type = 'income';
  }

  // Strip leading "+", trailing/leading spaces around the amount slot.
  const beforeRaw = trimmed.slice(0, matchIndex).replace(/^[+\-]/, '').trim();
  const afterRaw = trimmed.slice(matchEnd).trim();

  // Description prefers the text before the amount; falls back to
  // after when the line started with the amount.
  let description = beforeRaw || afterRaw;
  let remainingTokens = (beforeRaw ? afterRaw : '').trim();

  // Find a category by case-insensitive name match against the
  // remaining tokens. If we matched against `afterRaw` for description,
  // try the line's other tokens as the category source.
  let categoryId: string | null = null;
  const candidateText = remainingTokens || (beforeRaw ? afterRaw : '');
  if (candidateText) {
    const tokens = candidateText.split(/\s+/);
    const lookup = userCategories.map((c) => ({
      id: c.id,
      lower: c.name.toLowerCase(),
    }));
    // Prefer multi-word matches by trying progressively longer tail
    // joins ("Olive Garden" beats just "Olive").
    outer: for (let i = 0; i < tokens.length; i++) {
      for (let j = tokens.length; j > i; j--) {
        const phrase = tokens.slice(i, j).join(' ').toLowerCase();
        const hit = lookup.find((c) => c.lower === phrase);
        if (hit) {
          categoryId = hit.id;
          // Strip the matched tokens from the description fallback so
          // it doesn't contaminate the user-facing label.
          if (!beforeRaw) {
            description = [...tokens.slice(0, i), ...tokens.slice(j)]
              .join(' ')
              .trim() || description;
          }
          break outer;
        }
      }
    }
  }

  description = description.trim();
  const suggestedCategoryId = description
    ? suggestCategoryFromMerchant(description, userCategories)
    : null;

  const valid = description.length > 0 && amount > 0;
  let warning: string | undefined;
  if (!valid) {
    if (description.length === 0) warning = 'No description';
    else if (!(amount > 0)) warning = 'Amount must be greater than zero';
  }

  return {
    rawLine,
    description,
    amount: Number.isFinite(amount) ? amount : null,
    categoryId,
    suggestedCategoryId,
    type,
    valid,
    warning,
  };
}

export function parseBulkText(
  text: string,
  userCategories: Category[],
): ParsedDraft[] {
  return text
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0)
    .map((line) => parseLine(line, userCategories));
}
